import { useLocation } from "wouter";
import { Logo } from "@/components/Logo";
import QuoteCarousel from "@/components/QuoteCarousel";

const SAGE = "#A8B5A2";

const SECTION_STYLE: React.CSSProperties = {
  marginBottom: "40px",
};

const H2_STYLE: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 400,
  letterSpacing: "-0.01em",
  marginBottom: "12px",
  color: "#0a0a0a",
};

const P_STYLE: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 300,
  lineHeight: 1.75,
  color: "#3a3a3a",
  marginBottom: "12px",
};

const UL_STYLE: React.CSSProperties = {
  paddingLeft: "20px",
  marginBottom: "12px",
};

const LI_STYLE: React.CSSProperties = {
  fontSize: "15px",
  fontWeight: 300,
  lineHeight: 1.75,
  color: "#3a3a3a",
  marginBottom: "6px",
};

export default function PrivacyPolicy() {
  const [, navigate] = useLocation();

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa", fontFamily: "'Inter', sans-serif" }}>
      {/* Navbar */}
      <nav style={{
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        background: "rgba(252,252,252,0.95)",
        backdropFilter: "blur(16px)",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}>
        <div style={{ maxWidth: "1152px", margin: "0 auto", padding: "0 20px", height: "64px", display: "flex", alignItems: "center" }}>
          <button
            onClick={() => navigate("/")}
            style={{ display: "flex", alignItems: "center", gap: "10px", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <Logo iconSize={30} />
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "64px 20px 80px" }}>
        <p style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.1em", textTransform: "uppercase", color: SAGE, marginBottom: "12px" }}>
          Legal
        </p>
        <h1 style={{ fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 200, letterSpacing: "-0.03em", marginBottom: "8px" }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: "14px", fontWeight: 300, color: "#888", marginBottom: "48px" }}>
          Last updated: April 26, 2026
        </p>

        <div style={SECTION_STYLE}>
          <p style={P_STYLE}>
            DebtBridge Foundation ("DebtBridge," "we," "us," or "our") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, who we share it with, and your rights regarding your data. It applies to all users of our website and services.
          </p>
          <p style={P_STYLE}>
            Please read this policy carefully. By using DebtBridge, you agree to the practices described here.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>1. Information We Collect</h2>
          <p style={P_STYLE}>We collect the following categories of information:</p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}><strong>Identity & contact data:</strong> Name and email address, collected when you create an account or make a donation.</li>
            <li style={LI_STYLE}><strong>Financial data:</strong> Donation amounts, payment method metadata (processed by Stripe — we never store full card numbers). Stripe stores payment details on our behalf under PCI DSS standards.</li>
            <li style={LI_STYLE}><strong>Debt documents:</strong> If you are a debtor, you may upload debt notice documents (typically PDFs). These documents contain personal financial information such as creditor name, account balance, and employer details.</li>
            <li style={LI_STYLE}><strong>OCR-extracted data:</strong> We use Google Cloud Vision API to extract text from uploaded debt documents. Extracted fields (creditor, balance, employer) are stored in our database to facilitate debt relief processing.</li>
            <li style={LI_STYLE}><strong>Account metadata:</strong> Role (donor or debtor), account creation date, and user preferences.</li>
            <li style={LI_STYLE}><strong>Usage data:</strong> Standard server logs including IP address, browser type, and pages visited, collected automatically when you use our site.</li>
            <li style={LI_STYLE}>
              <strong>Tax Identification Number (SSN or ITIN):</strong> If you are a debtor whose debt is forgiven, the IRS requires us to file Form 1099-C (Cancellation of Debt) and include your Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN). We collect this number only when you voluntarily provide it, after you have read and accepted the explicit consent disclosure in our dashboard. Your TIN is encrypted at rest using AES-256-GCM with a key stored separately from the database; a one-way HMAC-SHA-256 hash is stored alongside it solely to detect duplicate submissions across accounts. The raw TIN is never returned in any API response, logged, or transmitted to any third party except the IRS as required by law.
            </li>
          </ul>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>2. How We Use Your Information</h2>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}>To process donations and facilitate debt relief payouts.</li>
            <li style={LI_STYLE}>To generate and issue tax donation receipts and debt forgiveness letters.</li>
            <li style={LI_STYLE}>To send transactional emails (donation confirmations, debt clearance notices).</li>
            <li style={LI_STYLE}>To verify debtor eligibility and prioritize relief queues.</li>
            <li style={LI_STYLE}>To maintain audit trails and comply with legal obligations.</li>
            <li style={LI_STYLE}>To detect and prevent fraud.</li>
            <li style={LI_STYLE}>To improve our services and website experience.</li>
          </ul>
          <p style={P_STYLE}>We do not sell your personal information to third parties. We do not use your data for advertising purposes.</p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>3. Third-Party Processors</h2>
          <p style={P_STYLE}>We share data with the following service providers only as necessary to deliver our services:</p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}><strong>Stripe:</strong> Payment processing. Stripe stores payment method details and processes all transactions. Governed by Stripe's Privacy Policy and PCI DSS Level 1 compliance.</li>
            <li style={LI_STYLE}><strong>Clerk:</strong> User authentication and identity management. Clerk processes your email and authentication credentials. Governed by Clerk's Privacy Policy.</li>
            <li style={LI_STYLE}><strong>Google Cloud Vision API:</strong> Optical character recognition (OCR) of uploaded debt documents. Document content is transmitted to Google for text extraction only and is not used to train Google's AI models under our enterprise terms.</li>
            <li style={LI_STYLE}><strong>Resend:</strong> Transactional email delivery. Your email address and name are shared to send confirmations and notices.</li>
          </ul>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>4. GLBA & FCRA Disclosures</h2>
          <p style={P_STYLE}>
            As an organization that receives and processes consumer financial information, DebtBridge complies with applicable provisions of the Gramm-Leach-Bliley Act (GLBA) and the Fair Credit Reporting Act (FCRA).
          </p>
          <p style={P_STYLE}>
            <strong>GLBA:</strong> We protect the financial information you provide using administrative, technical, and physical safeguards. We limit sharing of your financial data to what is necessary to provide the debt relief services you have requested.
          </p>
          <p style={P_STYLE}>
            <strong>FCRA:</strong> When you submit a debt document to DebtBridge, you consent to DebtBridge using that consumer financial information to evaluate and facilitate relief on your behalf. We may verify submitted information with creditors as part of the relief process. We do not report to consumer reporting agencies or resell consumer financial information.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>5. Your Privacy Rights</h2>
          <p style={{ ...P_STYLE, fontWeight: 400 }}>California Residents (CCPA)</p>
          <p style={P_STYLE}>California residents have the right to:</p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}>Know what personal information we collect, use, disclose, or sell.</li>
            <li style={LI_STYLE}>Request deletion of your personal information (subject to legal exceptions).</li>
            <li style={LI_STYLE}>Opt out of the sale of personal information (we do not sell personal information).</li>
            <li style={LI_STYLE}>Non-discrimination for exercising CCPA rights.</li>
          </ul>
          <p style={{ ...P_STYLE, fontWeight: 400 }}>EEA & UK Residents (GDPR)</p>
          <p style={P_STYLE}>If you are located in the European Economic Area or United Kingdom, you have the right to:</p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}>Access, rectify, or erase your personal data.</li>
            <li style={LI_STYLE}>Restrict or object to processing.</li>
            <li style={LI_STYLE}>Data portability.</li>
            <li style={LI_STYLE}>Lodge a complaint with your local supervisory authority.</li>
          </ul>
          <p style={P_STYLE}>To exercise any privacy right, contact us at <a href="mailto:privacy@debtbridge.org" style={{ color: SAGE }}>privacy@debtbridge.org</a>.</p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>6. Data Retention</h2>
          <p style={P_STYLE}>
            We retain personal data for as long as necessary to fulfill the purposes described in this policy, or as required by law:
          </p>
          <ul style={UL_STYLE}>
            <li style={LI_STYLE}><strong>Donation records:</strong> 7 years (IRS nonprofit recordkeeping requirements).</li>
            <li style={LI_STYLE}><strong>Tax Identification Numbers (SSN/ITIN):</strong> Retained for 7 years from the date of the associated Form 1099-C filing, as required by IRS nonprofit recordkeeping rules (IRC § 6001).</li>
            <li style={LI_STYLE}><strong>Debt documents and OCR data:</strong> Retained while your account is active and for 3 years after account closure, or as required by applicable debt law.</li>
            <li style={LI_STYLE}><strong>Account data:</strong> Retained while your account is active. Deleted within 90 days of a verified deletion request, subject to legal holds.</li>
            <li style={LI_STYLE}><strong>Server logs:</strong> 90 days.</li>
          </ul>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>7. Security</h2>
          <p style={P_STYLE}>
            We use industry-standard security measures including TLS encryption in transit, encrypted database storage, and role-based access controls. All payment processing is handled by Stripe, which is PCI DSS Level 1 certified. We never store raw card data.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>8. Cookies</h2>
          <p style={P_STYLE}>
            We use essential cookies to maintain your login session (managed by Clerk) and a single consent cookie (<code>db_cookie_consent</code>) to remember your cookie preferences. We do not use third-party advertising cookies or cross-site tracking.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>9. Email Communications</h2>
          <p style={P_STYLE}>
            We send transactional emails (donation confirmations, debt clearance notifications, receipts). These are necessary to deliver the service and cannot be unsubscribed from while your account is active. If you wish to stop receiving all emails, you may close your account by contacting us. All emails include an unsubscribe link for optional marketing communications.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>10. Children's Privacy</h2>
          <p style={P_STYLE}>
            DebtBridge is not directed to individuals under 18. We do not knowingly collect personal information from minors. If you believe a minor has provided information, contact us immediately.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>11. Changes to This Policy</h2>
          <p style={P_STYLE}>
            We may update this Privacy Policy from time to time. Material changes will be communicated by email and by updating the "Last updated" date above. Continued use of DebtBridge after changes constitutes acceptance.
          </p>
        </div>

        <div style={SECTION_STYLE}>
          <h2 style={H2_STYLE}>12. Contact Us</h2>
          <p style={P_STYLE}>
            For privacy inquiries, data requests, or concerns:<br />
            <strong>DebtBridge Foundation</strong><br />
            Email: <a href="mailto:privacy@debtbridge.org" style={{ color: SAGE }}>privacy@debtbridge.org</a><br />
            EIN: pending
          </p>
        </div>
      </div>

      <QuoteCarousel />

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(0,0,0,0.06)", padding: "28px 20px", textAlign: "center" }}>
        <p style={{ fontSize: "12px", fontWeight: 300, color: "#999" }}>
          © 2026 DebtBridge Foundation · EIN: pending ·{" "}
          <a href="/privacy" style={{ color: "#999", textDecoration: "underline" }}>Privacy</a>
          {" · "}
          <a href="/terms" style={{ color: "#999", textDecoration: "underline" }}>Terms</a>
        </p>
      </footer>
    </div>
  );
}
